import React, { useState, useRef, useEffect } from 'react';
import { BoundingBoxItem } from '../../types';
import ProductTooltip from '../ProductTooltip';
import './CombinationCard.css';

export interface CombinationCardProps {
  imageUrl?: string;
  imageData?: string;
  isLoading?: boolean;
  onImageClick?: () => void;
  children?: React.ReactNode;
  bounding_boxes?: BoundingBoxItem[];
  products?: any[];
}

const CombinationCard: React.FC<CombinationCardProps> = ({
  imageUrl,
  imageData,
  isLoading,
  onImageClick,
  children,
  bounding_boxes = [],
  products = [],
}) => {
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const imageSrc = imageUrl || (imageData ? `data:image/png;base64,${imageData}` : '');

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveTooltipId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDotClick = (e: React.MouseEvent, productId: string) => {
    e.stopPropagation(); // Stop zoom modal from opening

    // If clicking same product, close it
    if (activeTooltipId === productId) {
      setActiveTooltipId(null);
      return;
    }

    // Start rotation
    setRotatingId(productId);

    // Wait for rotation to finish (approx 300ms) before showing tooltip
    setTimeout(() => {
      setRotatingId(null);
      setActiveTooltipId(productId);
    }, 400); // 400ms matches CSS transition
  };

  const getProductById = (id: string) => {
    return products.find(p =>
      (p.sku && p.sku === id) ||
      (p.id && p.id === id) ||
      (p.productId && p.productId === id)
    );
  };

  return (
    <div className="combination-card" ref={containerRef}>
      <div className="combination-image-container">
        {imageSrc && !isLoading && (
          <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
            <img
              className="combination-image"
              src={imageSrc}
              alt="AI Kombin Önerisi"
              onClick={onImageClick}
            />

            {/* Render Bounding Box Dots */}
            {bounding_boxes.map((item, index) => {
              const { x_min, y_min, x_max, y_max } = item.bounding_box;

              // Calculate center position in percentages
              const centerX = ((x_min + x_max) / 2) * 100;
              const centerY = ((y_min + y_max) / 2) * 100;

              // Find associated product
              const product = getProductById(item.product_id);

              // If no product found, maybe it's using the index fallback from backend
              // Try to fallback to index if product_id is like "product_0"
              const productByIndex = !product && item.product_id.startsWith('product_')
                ? products[parseInt(item.product_id.split('_')[1])]
                : null;

              const displayProduct = product || productByIndex;

              if (!displayProduct) return null;

              return (
                <React.Fragment key={index}>
                  <div
                    className={`bounding-box-dot ${activeTooltipId === item.product_id ? 'active' : ''} ${rotatingId === item.product_id ? 'rotating' : ''}`}
                    style={{
                      left: `${centerX}%`,
                      top: `${centerY}%`
                    }}
                    onClick={(e) => handleDotClick(e, item.product_id)}
                    title={displayProduct.name || displayProduct.title || item.category}
                  />

                  {activeTooltipId === item.product_id && (
                    <ProductTooltip
                      product={displayProduct}
                      position={{ x: centerX, y: centerY }}
                      onClose={() => setActiveTooltipId(null)}
                      onClick={() => {
                        // Optional: Navigate to product detail on tooltip click
                        // For now just keep it open or close it
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default CombinationCard;
